"use client";

import { flexRender, type Header } from "@tanstack/react-table";
import type { RowData } from "@tanstack/react-table";
import type { RecordGridFeatures } from "@/components/ui/record-table/grid/record-grid-features";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "@/components/icons";
import { cn } from "@/lib/utils";
import { headerAlignmentClass, headerClassName } from "./column-meta";
import type { RecordObjectDef, RecordSort } from "../types";

interface RecordGridHeadCellProps<TData extends RowData> {
  header: Header<RecordGridFeatures, TData, unknown>;
  object: RecordObjectDef<TData>;
  sort: RecordSort | undefined;
  onSortChange: (sort: RecordSort | undefined) => void;
  reorderable: boolean;
  width?: number;
}

export function RecordGridHeadCell<TData extends RowData>({
  header,
  object,
  sort,
  onSortChange,
  reorderable,
  width,
}: RecordGridHeadCellProps<TData>) {
  const columnId = header.column.id;
  const field = object.fieldsById.get(columnId);
  const isSortable = field?.sortable === true;
  const isPinned = header.column.getIsPinned() === "start";
  const activeDirection = sort?.field === columnId ? sort.direction : undefined;

  const canDrag = reorderable && !isPinned;
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: columnId, disabled: !canDrag });

  const toggleSort = () => {
    if (!isSortable) return;
    if (activeDirection === "asc") {
      onSortChange({ field: columnId, direction: "desc" });
    } else if (activeDirection === "desc") {
      onSortChange(undefined);
    } else {
      onSortChange({ field: columnId, direction: "asc" });
    }
  };

  const SortIcon =
    activeDirection === "asc"
      ? ArrowUp
      : activeDirection === "desc"
        ? ArrowDown
        : ChevronsUpDown;

  const meta = header.column.columnDef.meta;
  const labelAlignment = cn(headerAlignmentClass(meta), headerClassName(meta));

  return (
    <th
      ref={setNodeRef}
      colSpan={header.colSpan}
      style={{
        width: width ?? header.getSize(),
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isPinned ? { left: header.column.getStart("start") } : {}),
      }}
      className={cn(
        "group/head sticky top-0 border-b border-border bg-surface-2",
        "h-[var(--table-header-height)] px-[var(--table-cell-px)] py-[var(--table-header-py)]",
        "text-left align-middle text-xs font-medium whitespace-nowrap text-muted-foreground",
        isDragging ? "z-40 opacity-80" : isPinned ? "z-30" : "z-20",
        isPinned &&
          "in-data-[scrolled-x]:shadow-[inset_-1px_0_0_var(--color-border)]",
      )}
    >
      {header.isPlaceholder ? null : (
        <div className="relative flex items-center gap-1">
          {isSortable ? (
            <button
              type="button"
              onClick={toggleSort}
              {...(canDrag ? { ...attributes, ...listeners } : {})}
              className={cn(
                "flex min-w-0 flex-1 items-center gap-1 hover:text-foreground",
                canDrag
                  ? "cursor-grab active:cursor-grabbing"
                  : "cursor-default",
                labelAlignment,
              )}
            >
              <span className="truncate">
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </span>
              <SortIcon
                className={cn(
                  "h-3 w-3 shrink-0 transition-opacity",
                  activeDirection
                    ? "text-foreground opacity-100"
                    : "opacity-0 group-hover/head:opacity-60",
                )}
              />
            </button>
          ) : (
            <div
              {...(canDrag ? { ...attributes, ...listeners } : {})}
              className={cn(
                "flex min-w-0 flex-1 items-center gap-1",
                canDrag
                  ? "cursor-grab active:cursor-grabbing"
                  : "cursor-default",
                labelAlignment,
              )}
            >
              <span className="truncate">
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </span>
            </div>
          )}
          {header.column.getCanResize() ? (
            <span
              onMouseDown={header.getResizeHandler()}
              onTouchStart={header.getResizeHandler()}
              onClick={(event) => event.stopPropagation()}
              onDoubleClick={() => header.column.resetSize()}
              title="Drag to resize; double-click to reset"
              style={{
                transform: header.column.getIsResizing()
                  ? `translateX(${header.getContext().table.atoms.columnResizing.get().deltaOffset ?? 0}px)`
                  : undefined,
              }}
              className="absolute -top-2 -right-2 z-10 h-[var(--table-header-height)] w-4 cursor-col-resize touch-none select-none after:absolute after:top-2 after:bottom-2 after:left-1/2 after:w-px after:bg-border after:opacity-0 after:content-[''] group-hover/head:after:opacity-100 hover:after:w-0.5 hover:after:bg-action-fill hover:after:opacity-100"
            />
          ) : null}
        </div>
      )}
    </th>
  );
}
